import React, { forwardRef } from 'react'
import { Tooltip } from '../simple/tooltip'

export interface MiniPieData {
  slices: { value: number; color: string; legend?: { title: string; value: number; position?: number } }[]
  ringColor?: string
  backgroundColor?: string
}

interface MiniPieProps extends React.HTMLAttributes<HTMLDivElement> {
  data: MiniPieData
  size?: number
}

export const MiniPie = forwardRef<HTMLDivElement, MiniPieProps>(({ data, size = 20, ...props }, ref) => {
  const backgroundColor = data.backgroundColor || 'transparent'
  const ringColor = data.ringColor || 'transparent'
  const gradientPoints = data.slices
    .flatMap((slice) => [
      { color: slice.color, value: 0 },
      { color: slice.color, value: slice.value },
    ])
    .concat({ color: backgroundColor, value: 0 }, { color: backgroundColor, value: 1 })

  const legendSlices = data.slices
    .filter((slice) => !!slice.legend)
    .sort((a, b) => (a.legend!.position || 0) - (b.legend!.position || 0))
  const legend =
    legendSlices.length > 0 ? (
      <div className="flex flex-col">
        {legendSlices.map((slice, i) => (
          <div key={i} className="flex items-center gap-2">
            <div style={{ width: 10, height: 10, backgroundColor: slice.color }} />
            <div>
              {slice.legend!.title} <b>{slice.legend!.value}</b>
            </div>
          </div>
        ))}
      </div>
    ) : null

  const pie = (
    <div
      ref={ref}
      {...props}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderColor: ringColor,
        borderStyle: 'solid',
        borderWidth: 2,
        borderRadius: '100%',
        display: 'flex',
        ...props.style,
      }}
    >
      <div
        style={{
          margin: 2,
          flexGrow: 1,
          borderRadius: '100%',
          background: `conic-gradient(from 0deg,${gradientPoints
            .map((point) => `${point.color} ${Math.round(100 * point.value)}%`)
            .join(',')})`,
        }}
      />
    </div>
  )

  return legend ? <Tooltip text={legend}>{pie}</Tooltip> : pie
})
